/**
 * Chart of Accounts Page
 *
 * Account categories management in a hierarchical tree structure.
 * Uses real data from the accounting store (database).
 */

import React, { useState, useEffect } from 'react';
import type { AccountCategory } from '../../api';
import { ChartOfAccountsForm } from '../forms';
import type { ChartOfAccountsFormData } from '../forms';
import { useAccountingStore } from '../../state/accountingState';
import '../accounting.css';

// =============================================================================
// TYPES
// =============================================================================

interface ChartOfAccountsPageProps {
  onNavigate?: (path: string) => void;
  onBack?: () => void;
}

interface TreeNode extends AccountCategory {
  children: TreeNode[];
  expanded?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const CATEGORY_LABELS: Record<AccountCategory['type'], string> = {
  revenue: 'Ricavi',
  expense: 'Costi',
  asset: 'Attivita',
  liability: 'Passivita',
  equity: 'Patrimonio netto',
};

// NOTE: No mock data - data comes from database via accounting store

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function buildTree(categories: AccountCategory[]): TreeNode[] {
  const nodeMap = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  // Create nodes
  categories.forEach((cat) => {
    nodeMap.set(cat.id, { ...cat, children: [], expanded: true });
  });

  // Build tree
  categories.forEach((cat) => {
    const node = nodeMap.get(cat.id)!;
    if (cat.parentId) {
      const parent = nodeMap.get(cat.parentId);
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  // Sort by code
  const sortNodes = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.code.localeCompare(b.code));
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);

  return roots;
}

// =============================================================================
// COMPONENTS
// =============================================================================

function TreeNodeComponent({
  node,
  level = 0,
  onToggle,
  onEdit,
}: {
  node: TreeNode;
  level?: number;
  onToggle: (id: string) => void;
  onEdit: (node: TreeNode) => void;
}) {
  const hasChildren = node.children.length > 0;

  return (
    <div className="accounting-tree">
      <div className="accounting-tree__node">
        {/* Expand/Collapse */}
        <button
          onClick={() => onToggle(node.id)}
        >
          <svg
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Code */}
        <span>
          {node.code}
        </span>

        {/* Name */}
        <span className="accounting-content">{node.name}</span>

        {/* Type badge */}
        <span>
          {CATEGORY_LABELS[node.type]}
        </span>

        {/* Status */}
        {!node.isActive && (
          <span>Inattivo</span>
        )}

        {/* Edit button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(node);
          }}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
      </div>

      {/* Children */}
      {node.expanded && node.children.length > 0 && (
        <div className="accounting-tree__children">
          {node.children.map((child) => (
            <TreeNodeComponent
              key={child.id}
              node={child}
              level={level + 1}
              onToggle={onToggle}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function ChartOfAccountsPage({ onNavigate, onBack }: ChartOfAccountsPageProps) {
  const {
    accountCategories: categories,
    loadAccountCategories,
    createAccountCategory,
    updateAccountCategory,
    deleteAccountCategory,
    loading,
  } = useAccountingStore();

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<AccountCategory['type'] | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AccountCategory | null>(null);

  // Load categories on mount
  useEffect(() => {
    loadAccountCategories();
  }, [loadAccountCategories]);

  // Update expanded IDs when categories load
  useEffect(() => {
    if (categories.length > 0 && expandedIds.size === 0) {
      setExpandedIds(new Set(categories.filter((c) => !c.parentId).map((c) => c.id)));
    }
  }, [categories, expandedIds.size]);

  const filteredCategories = categories.filter(
    (c) => typeFilter === 'all' || c.type === typeFilter
  );

  const tree = buildTree(filteredCategories).map((node) => ({
    ...node,
    expanded: expandedIds.has(node.id),
    children: node.children.map((child) => ({
      ...child,
      expanded: expandedIds.has(child.id),
    })),
  }));

  const handleToggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleEdit = (node: TreeNode) => {
    setEditingEntry(node);
    setShowForm(true);
  };

  const stats = {
    revenue: categories.filter((c) => c.type === 'revenue').length,
    expense: categories.filter((c) => c.type === 'expense').length,
    asset: categories.filter((c) => c.type === 'asset').length,
    liability: categories.filter((c) => c.type === 'liability').length,
    equity: categories.filter((c) => c.type === 'equity').length,
  };

  return (
    <div className="accounting-page accounting-page--narrow">
      {/* Stats */}
      <div className="accounting-filters">
        {Object.entries(stats).map(([type, count]) => (
          <button
            key={type}
            onClick={() => setTypeFilter(typeFilter === type ? 'all' : (type as AccountCategory['type']))}
            className="accounting-flex--row accounting-flex--gap-sm"
          >
            <span>
              {CATEGORY_LABELS[type as AccountCategory['type']]}
            </span>
            <span>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="accounting-toolbar">
        <button
          onClick={() => setExpandedIds(new Set(categories.map((c) => c.id)))}
        >
          Espandi tutto
        </button>
        <div className="accounting-toolbar__spacer" />
        <button
          onClick={() => {
            setEditingEntry(null);
            setShowForm(true);
          }}
          className="accounting-flex--row accounting-flex--gap-sm"
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nuovo conto
        </button>
      </div>

      {/* Tree */}
      <div className="accounting-tree">
        {tree.length === 0 ? (
          <div className="accounting-empty">
            Nessuna categoria trovata
          </div>
        ) : (
          tree.map((node) => (
            <TreeNodeComponent
              key={node.id}
              node={node as TreeNode}
              onToggle={handleToggle}
              onEdit={handleEdit}
            />
          ))
        )}
      </div>

      {/* Chart of Accounts Form Modal */}
      {showForm && (
        <ChartOfAccountsForm
          entry={editingEntry ? {
            id: editingEntry.id,
            code: editingEntry.code,
            name: editingEntry.name,
            category: mapLocalTypeToFormCategory(editingEntry.type),
            parentId: editingEntry.parentId || null,
            description: '',
            isLeaf: !categories.some((c) => c.parentId === editingEntry.id),
          } : undefined}
          onSave={(data: ChartOfAccountsFormData) => {
            const formCategoryToLocalType = (cat: string): AccountCategory['type'] => {
              const map: Record<string, AccountCategory['type']> = {
                assets: 'asset',
                liabilities: 'liability',
                equity: 'equity',
                revenue: 'revenue',
                expenses: 'expense',
              };
              return map[cat] || 'expense';
            };

            if (editingEntry) {
              // Update existing entry via store
              updateAccountCategory(editingEntry.id, {
                code: data.code,
                name: data.name,
                type: formCategoryToLocalType(data.category),
                parentId: data.parentId || undefined,
              });
            } else {
              // Create new entry via store
              createAccountCategory({
                code: data.code,
                name: data.name,
                type: formCategoryToLocalType(data.category),
                parentId: data.parentId || undefined,
                isActive: true,
              });
            }
            setShowForm(false);
            setEditingEntry(null);
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingEntry(null);
          }}
        />
      )}
    </div>
  );
}

// Helper function to map local type to form category
function mapLocalTypeToFormCategory(type: AccountCategory['type']): 'assets' | 'liabilities' | 'equity' | 'revenue' | 'expenses' {
  const map: Record<AccountCategory['type'], 'assets' | 'liabilities' | 'equity' | 'revenue' | 'expenses'> = {
    asset: 'assets',
    liability: 'liabilities',
    equity: 'equity',
    revenue: 'revenue',
    expense: 'expenses',
  };
  return map[type];
}

export type { ChartOfAccountsPageProps };
